import type { DashboardDto, PollSummaryDto, TaskDto } from '@ukinfo/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { EVENT_TYPE_LABELS, formatShort, relativeDays } from '../lib/format';
import type { Team } from '../lib/sports';
import { PageHeader } from '../components/PageHeader';
import { PushPrompt } from '../components/PushPrompt';
import { TaskDialog } from '../components/TaskDialog';
import {
  Badge,
  Button,
  Card,
  CardBody,
  EmptyState,
  ErrorBlock,
  LoadingBlock,
  SectionTitle,
  useToast,
} from '../components/ui';

/**
 * Dashboard "Heute".
 * Beantwortet: Was ist heute bzw. demnaechst fuer mich relevant?
 */
export function TodayPage(): JSX.Element {
  const { can } = useAuth();
  const [taskDialog, setTaskDialog] = useState(false);
  const [completed, setCompleted] = useState<TaskDto | null>(null);
  const queryClient = useQueryClient();
  const toast = useToast();
  const finishTask = useMutation({
    mutationFn: ({ task, undo }: { task: TaskDto; undo?: boolean }) => api.patch<TaskDto>(`/api/tasks/${task.id}`, { status: undo ? task.status : 'DONE' }),
    onSuccess: async (_result, { task, undo }) => {
      setCompleted(undo ? null : task);
      toast.show(undo ? 'Aufgabe wieder geöffnet' : 'Aufgabe erledigt');
      await queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      await queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
    onError: () => toast.show('Die Aufgabe konnte nicht geändert werden. Bitte versuche es erneut.', 'error'),
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => api.get<DashboardDto>('/api/dashboard'),
  });

  // Fuenf reichen fuer den Tag; der Rest steht unter "Alle Aufgaben".
  const naechsteAufgaben = [...(data?.myTasks.items ?? [])]
    .sort((a, b) => Number(b.isOverdue) - Number(a.isOverdue) || (a.dueAt ?? '9999').localeCompare(b.dueAt ?? '9999'))
    .slice(0, 5);

  if (isLoading) return <LoadingBlock />;
  if (error) return <ErrorBlock error={error} />;
  if (!data) return <EmptyState title="Keine Daten" />;

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={`${data.greeting}, ${data.personName}`}
        description="Was heute und demnächst ansteht"
        actions={can('tasks', 'create') ? <Button onClick={() => setTaskDialog(true)}>Neue Aufgabe</Button> : undefined}
      />

      <PushPrompt channel="account" />

      <OffeneUmfragen />

      <div className="order-1 grid min-w-0 grid-cols-1 gap-5 lg:grid-cols-2">
        {/* Meine Aufgaben */}
        {can('tasks', 'read') && <Card>
          <CardBody>
            <SectionTitle
              title="Meine Aufgaben"
              subtitle={`${data.myTasks.open} offen, ${data.myTasks.overdue} überfällig`}
              action={
                <Link to="/aufgaben?mine=1" className="text-sm font-medium text-brand-700">
                  Alle Aufgaben
                </Link>
              }
            />
            {completed && <div role="status" className="mb-3 rounded-lg bg-emerald-50 p-3 text-sm text-emerald-900">
              „{completed.title}“ ist erledigt.
              <Button variant="secondary" className="ml-2" onClick={() => finishTask.mutate({ task: completed, undo: true })} disabled={finishTask.isPending}>Rückgängig</Button>
            </div>}
            {data.myTasks.items.length === 0 ? (
              <p className="py-4 text-sm text-slate-500">
                Aktuell liegt nichts bei dir – weder zugewiesen noch offen in deinem Bereich.
              </p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {naechsteAufgaben.map((task) => (
                  <li key={task.id} className="flex items-center gap-2 py-1">
                    <Link
                      to={`/aufgaben/${task.id}`}
                      className="flex min-w-0 flex-1 flex-wrap items-start justify-between gap-x-3 gap-y-1 py-2"
                    >
                      <span className="min-w-0">
                        <span className="block break-words font-medium text-slate-800">
                          {task.title}
                        </span>
                        <span className="block truncate text-xs text-slate-500">
                          {task.organisationUnits.map((u) => u.name).join(', ')}
                          {/* Unzugewiesene Aufgaben stehen hier, weil sie den
                              eigenen Bereich betreffen - das muss man sehen. */}
                          {task.assignees.length === 0 && ' · nicht zugewiesen'}
                        </span>
                      </span>
                      {task.dueAt && (
                        <Badge tone={task.isOverdue ? 'danger' : 'neutral'}>
                          Fällig: {relativeDays(task.dueAt)}
                        </Badge>
                      )}
                    </Link>
                    {task.canChangeStatus && (
                      <button
                        type="button"
                        onClick={() => finishTask.mutate({ task })}
                        disabled={finishTask.isPending}
                        aria-label={`Aufgabe „${task.title}“ erledigen`}
                        title="Erledigt"
                        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-slate-300 text-lg text-slate-400 transition-colors hover:border-emerald-500 hover:bg-emerald-50 hover:text-emerald-600 disabled:opacity-50"
                      >
                        <span aria-hidden="true">✓</span>
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {data.myTasks.items.length > naechsteAufgaben.length && (
              <Link to="/aufgaben?mine=1" className="mt-1 block text-sm font-medium text-brand-700 hover:underline">
                {data.myTasks.items.length - naechsteAufgaben.length} weitere Aufgaben
              </Link>
            )}
          </CardBody>
        </Card>}

        {/* Mannschaften: von hier aus in einem Griff in die eigene Mannschaft */}
        {can('sports_participation', 'read') && <MeinSport />}

        {/* Naechste Termine */}
        {can('calendar', 'read') && <Card>
          <CardBody>
            <SectionTitle
              title="Nächste Termine"
              subtitle="Die kommenden 14 Tage"
              action={
                <Link to="/kalender" className="text-sm font-medium text-brand-700">
                  Kalender
                </Link>
              }
            />
            {data.upcomingEvents.length === 0 ? (
              <p className="py-4 text-sm text-slate-500">Keine Termine in den nächsten Tagen.</p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {data.upcomingEvents.map((item) => (
                  <li key={item.id} className="flex items-start justify-between gap-3 py-2.5">
                    <span className="min-w-0">
                      <span className="block truncate font-medium text-slate-800">
                        {item.title}
                      </span>
                      <span className="block truncate text-xs text-slate-500">
                        {formatShort(item.startAt)}
                        {item.location ? ` · ${item.location}` : ''}
                      </span>
                    </span>
                    <span className="flex shrink-0 flex-col items-end gap-1">
                      <Badge tone="info">
                        {EVENT_TYPE_LABELS[item.eventType] ?? item.eventType}
                      </Badge>
                      {item.sourceOfTruth === 'FOOTBALL_FEDERATION' && (
                        <Badge tone="brand" title="Wird vom Verband gepflegt">
                          🔄 Verband
                        </Badge>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>}

        {/* Freigaben */}
        {can('media', 'read') && data.needsMyApproval.total > 0 && (
          <Card>
            <CardBody>
              <SectionTitle
                title="Benötigt meine Freigabe"
                subtitle={`${data.needsMyApproval.total} offen`}
              />
              <ul className="divide-y divide-slate-100">
                {data.needsMyApproval.mediaPosts.map((post) => (
                  <li key={post.id}>
                    <Link
                      to={`/medien/beitraege/${post.id}`}
                      className="flex items-center justify-between gap-3 py-2.5"
                    >
                      <span className="truncate font-medium text-slate-800">{post.title}</span>
                      <Badge tone="warning">Medienbeitrag</Badge>
                    </Link>
                  </li>
                ))}
              </ul>
            </CardBody>
          </Card>
        )}

        {/* Demnaechst */}
        {can('sponsoring_contracts', 'read') && data.upcoming.length > 0 && (
          <Card>
            <CardBody>
              <SectionTitle title="Demnächst" subtitle="Fristen und auslaufende Verträge" />
              <ul className="divide-y divide-slate-100">
                {data.upcoming.map((item, index) => (
                  <li key={`${item.link}-${index}`}>
                    <Link to={item.link} className="flex items-start justify-between gap-3 py-2.5">
                      <span className="min-w-0">
                        <span className="block truncate font-medium text-slate-800">
                          {item.title}
                        </span>
                        <span className="block text-xs text-slate-500">{item.detail}</span>
                      </span>
                      <Badge tone={item.days < 14 ? 'warning' : 'neutral'}>{item.kind}</Badge>
                    </Link>
                  </li>
                ))}
              </ul>
            </CardBody>
          </Card>
        )}
      </div>

      <details className="order-3 rounded-xl border border-slate-200 bg-white p-4">
        <summary className="min-h-11 cursor-pointer py-2 font-medium text-brand-700">Hilfe: Wie fange ich an?</summary>
        <div className="space-y-2 pt-2 text-sm text-slate-600">
          <p>Unter „Meine Aufgaben“ siehst du, was ansteht. Tippe auf eine Aufgabe, um die Einzelheiten zu lesen. Wenn du fertig bist, wähle „Erledigt“.</p>
          <p>Unter „Nächste Termine“ siehst du, wann und wo etwas stattfindet. Im Kalender findest du weitere Termine.</p>
          <p>Du siehst nur die Bereiche und Schaltflächen, für die du berechtigt bist. Fehlt dir etwas, frage die für eure Zugänge zuständige Person im Verein.</p>
        </div>
      </details>

      {can('tasks', 'create') && <TaskDialog open={taskDialog} onClose={() => setTaskDialog(false)} />}
    </div>
  );
}

/** Umfragen, auf die noch meine Antwort fehlt. Ohne offene Umfrage kein Platzbedarf. */
function OffeneUmfragen(): JSX.Element | null {
  const { data } = useQuery({
    queryKey: ['polls'],
    queryFn: () => api.get<PollSummaryDto[]>('/api/polls'),
  });
  const pending = (data ?? []).filter((poll) => poll.isRecipient && poll.isOpen && !poll.myAnsweredAt);
  if (pending.length === 0) return null;
  return (
    <Card className="border-amber-200 bg-amber-50/60">
      <CardBody>
        <SectionTitle
          title={pending.length === 1 ? 'Eine Umfrage wartet auf dich' : `${pending.length} Umfragen warten auf dich`}
          action={<Link to="/umfragen" className="text-sm font-medium text-brand-700">Alle Umfragen</Link>}
        />
        <ul className="divide-y divide-amber-100">
          {pending.slice(0, 5).map((poll) => (
            <li key={poll.id}>
              <Link to={`/umfragen/${poll.id}`} className="flex flex-wrap items-center justify-between gap-2 py-2.5">
                <span className="min-w-0">
                  <span className="block break-words font-medium text-slate-800">{poll.title}</span>
                  <span className="block text-xs text-slate-500">{poll.organisationUnitName}</span>
                </span>
                {poll.closesAt && <Badge tone="warning">bis {relativeDays(poll.closesAt)}</Badge>}
              </Link>
            </li>
          ))}
        </ul>
      </CardBody>
    </Card>
  );
}

/**
 * Sport auf der Startseite: die eigenen Mannschaften mit ihrem naechsten
 * Termin, ein Griff zur Mannschaft. Wer nur eine Mannschaft hat, soll nicht
 * erst ueber die Sportuebersicht muessen.
 */
function MeinSport(): JSX.Element | null {
  const seasons = useQuery({
    queryKey: ['sports-seasons'],
    queryFn: () => api.get<{ id: string }[]>('/api/sports/seasons'),
  });
  const seasonId = seasons.data?.[0]?.id ?? '';
  const teams = useQuery({
    queryKey: ['sports-teams', 'heute', seasonId],
    queryFn: () => api.get<Team[]>('/api/sports/teams', { seasonId }),
    enabled: Boolean(seasonId),
  });
  const nachTermin = [...(teams.data ?? [])].sort(
    (a, b) => (a.nextSession?.startAt ?? '9999').localeCompare(b.nextSession?.startAt ?? '9999'),
  );
  const meine = nachTermin.slice(0, 3);
  if (meine.length === 0) return null;
  return (
    <Card>
      <CardBody>
        <SectionTitle
          title="Deine Mannschaften"
          subtitle={meine.length === 1 ? 'Ein Klick führt hinein' : 'Die mit dem nächsten Termin zuerst'}
          action={
            <Link to="/sport" className="text-sm font-medium text-brand-700">
              {nachTermin.length > meine.length ? `Alle ${nachTermin.length}` : 'Alle Mannschaften'}
            </Link>
          }
        />
        <ul className="divide-y divide-slate-100">
          {meine.map((team) => (
            <li key={team.id}>
              <Link to={`/sport/${team.id}`} className="flex items-center justify-between gap-3 py-2.5">
                <span className="min-w-0">
                  <span className="block truncate font-medium text-slate-800">{team.name}</span>
                  <span className="block truncate text-xs text-slate-500">
                    {team.nextSession
                      ? `${team.nextSession.kind === 'MATCH' ? 'Spiel' : 'Training'}: ${team.nextSession.title} · ${formatShort(team.nextSession.startAt)}`
                      : 'Kein Termin geplant'}
                  </span>
                </span>
                {team.canManage && <Badge tone="neutral">Du führst sie</Badge>}
              </Link>
            </li>
          ))}
        </ul>
      </CardBody>
    </Card>
  );
}
